import * as L from "leaflet";
import {
  Feature,
  JsonGeometry,
  LatLngBoundsSerialized,
  LatLngExpressionSerialized
} from "store/types";

function toLatLngBoundsAny(b: any | undefined): L.LatLngBounds | undefined {
  if (!b) return undefined;

  if (
    typeof b?.getSouthWest === "function" &&
    typeof b?.getNorthEast === "function"
  ) {
    return b as L.LatLngBounds;
  }

  if (
    Array.isArray(b) &&
    b.length === 2 &&
    Array.isArray(b[0]) &&
    Array.isArray(b[1])
  ) {
    return L.latLngBounds(b as L.LatLngBoundsLiteral);
  }

  if (
    Array.isArray(b) &&
    b.length === 4 &&
    b.every(n => typeof n === "number")
  ) {
    const [w, s, e, n] = b as [number, number, number, number];
    return L.latLngBounds([
      [s, w],
      [n, e]
    ]);
  }

  if (b?._southWest && b?._northEast) {
    return L.latLngBounds([
      [b._southWest.lat, b._southWest.lng],
      [b._northEast.lat, b._northEast.lng]
    ]);
  }

  return undefined;
}

function posToLeaflet(pos: any): L.LatLngExpression | undefined {
  if (!Array.isArray(pos) || pos.length < 2) return undefined;
  const [lat, lng, alt] = pos as [number, number, number?];
  if (typeof lat !== "number" || typeof lng !== "number") return undefined;
  return alt == null ? [lat, lng] : [lat, lng, alt];
}

function flattenCoordsToLeafletPoints(
  coords: any,
  out: L.LatLngExpression[] = []
): L.LatLngExpression[] {
  if (!coords) return out;

  // Leaflet LatLng objects
  if (
    coords &&
    typeof coords === "object" &&
    typeof coords.lat === "number" &&
    typeof coords.lng === "number"
  ) {
    out.push([coords.lat, coords.lng]);
    return out;
  }

  if (Array.isArray(coords) && typeof coords[0] === "number") {
    const p = posToLeaflet(coords);
    if (p) out.push(p);
    return out;
  }

  if (Array.isArray(coords)) {
    for (const c of coords) flattenCoordsToLeafletPoints(c, out);
  }

  return out;
}

function featureToBounds(f: Feature): L.LatLngBounds | undefined {
  const g: any = f.geometry || {};
  // Prefer bounds/bbox if present
  const b = toLatLngBoundsAny(g.bounds ?? (f as any).bbox);
  if (b && b.isValid()) return b;

  // Fallback: compute from coordinates
  const pts = flattenCoordsToLeafletPoints(g.coordinates);
  if (pts.length === 0) return undefined;
  const computed = L.latLngBounds(pts);
  return computed.isValid() ? computed : undefined;
}

export function unionBounds(features: Feature[]): L.LatLngBounds | undefined {
  const union = L.latLngBounds([]);
  let added = false;
  for (const f of features) {
    const b = featureToBounds(f);
    if (b && b.isValid()) {
      union.extend(b);
      added = true;
    }
  }
  return added && union.isValid() ? union : undefined;
}

export function sanitizeShapes(shapes: Feature[]): Feature[] {
  const serializeCoords = (coords: any): any => {
    if (coords == null) return coords;

    if (
      typeof coords === "object" &&
      !Array.isArray(coords) &&
      typeof (coords as any).lat === "number" &&
      typeof (coords as any).lng === "number"
    ) {
      const { lat, lng, alt } = coords as L.LatLng;
      return alt == null ? [lat, lng] : [lat, lng, alt];
    }

    if (Array.isArray(coords)) {
      return coords.map(serializeCoords);
    }

    return coords;
  };

  const serializeBounds = (b: any): LatLngBoundsSerialized | undefined => {
    if (!b) return undefined;

    if (
      typeof b.getSouthWest === "function" &&
      typeof b.getNorthEast === "function"
    ) {
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      return { southWest: [sw.lat, sw.lng], northEast: [ne.lat, ne.lng] };
    }

    if (typeof b.lat === "number" && typeof b.lng === "number") {
      return { southWest: [b.lat, b.lng], northEast: [b.lat, b.lng] };
    }

    if (b?._southWest && b?._northEast) {
      return {
        southWest: [b._southWest.lat, b._southWest.lng],
        northEast: [b._northEast.lat, b._northEast.lng]
      };
    }

    return undefined;
  };

  return shapes.map(f => {
    const g = f.geometry as JsonGeometry;
    const safeCoords = serializeCoords(g.coordinates);
    const safeBounds = serializeBounds((f.geometry as any).bounds);

    return {
      ...f,
      geometry: {
        ...g,
        coordinates: safeCoords,
        bounds: safeBounds
      }
    };
  });
}
